import {
  buildSearchBox,
  buildRecentQueriesList,
  Suggestion,
  RecentQueriesList,
  RecentQueriesState,
} from '@coveo/headless';
import { useCoveoEngine } from 'lib/coveo/coveo';
import { useRouter } from 'next/router';
import { ChangeEvent, useEffect, useRef, useState } from 'react';
import { AzBlueAtomicSearchBoxProps } from '../atomic-wrappers/AzBlueAtomicSearchBox';
import classNames from 'classnames';
import HeroIcon from 'components/helpers/Icons/HeroIcon/HeroIcon';
import { AzBlueHeadlessAutoSuggestDropdown } from './AzBlueHeadlessAutoSuggestDropdown';

interface SearchBoxProps {
  fields: AzBlueAtomicSearchBoxProps['fields'];
}

// This component is specifically used on the Search Page (e.g., /search) to render the main Coveo-powered search bar.
export const AzBlueHeadlessSearchPageBar = ({ fields }: SearchBoxProps) => {
  const router = useRouter();
  const coveoEngine = useCoveoEngine();
  const inputRef = useRef<HTMLInputElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);

  const [searchValue, setSearchValue] = useState('');
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [isSuggestionsVisible, setIsSuggestionsVisible] = useState(false);

  const controllerRef = useRef(
    buildSearchBox(coveoEngine, {
      options: {
        highlightOptions: {
          notMatchDelimiters: { open: '<strong>', close: '</strong>' },
          correctionDelimiters: { open: '<i>', close: '</i>' },
        },
        numberOfSuggestions: 8,
      },
    })
  );
  const controller = controllerRef.current;

  // Coveo Recent Queries Controller
  const [recentController] = useState<RecentQueriesList>(() =>
    buildRecentQueriesList(coveoEngine, { options: { maxLength: 10 } })
  );
  const [recentState, setRecentState] = useState<RecentQueriesState>(recentController.state);

  useEffect(() => {
    const unsubscribe = recentController.subscribe(() => setRecentState(recentController.state));
    return unsubscribe;
  }, [recentController]);

  const redirectUrl =
    router?.asPath.split('#')[0] === fields?.searchPage?.url ? undefined : fields?.searchPage?.url;

  const handleSubmit = (value?: string) => {
    const finalValue = (value ?? searchValue).trim();
    if (!finalValue) return;

    if (redirectUrl) {
      window.location.assign(`${redirectUrl}#q=${finalValue}`);
    } else {
      controller.updateText(finalValue);
      controller.submit(); // Triggers and logs the recent search
    }

    setIsSuggestionsVisible(false);
  };

  const handleInput = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e?.target?.value;
    setSearchValue(value);
    controller.updateText(value);
    controller.showSuggestions();
    setIsSuggestionsVisible(value.trim().length > 0);
  };

  const handleClear = () => {
    setSearchValue('');
    controller.clear();
    controller.showSuggestions();
    setIsSuggestionsVisible(false);
    controller.submit();
  };

  const handleBlur = () => {
    setTimeout(() => {
      const activeEl = document.activeElement;
      if (!wrapperRef.current?.contains(activeEl)) {
        setIsSuggestionsVisible(false);
      }
    }, 100);
  };

  useEffect(() => {
    return controller.subscribe(() => {
      inputRef.current?.focus();
      if (!controller.state.isLoading) {
        setSearchValue(controller.state.value);
      }
    });
  }, []);

  useEffect(() => {
    const unsubscribe = controller.subscribe(() => {
      setSuggestions(controller.state.suggestions);
    });
    return () => unsubscribe();
  }, [controller]);

  const isEnterKey = (e: React.KeyboardEvent<HTMLInputElement>) => e.key === 'Enter';

  return (
    <div
      ref={wrapperRef}
      onBlur={handleBlur}
      tabIndex={-1}
      className={classNames(
        'hover:ring',
        'hover:ring-blue',
        'flex',
        'group',
        'items-center',
        'overflow-visible',
        'relative',
        'rounded-lg',
        'transition-all',
        'w-full'
      )}
    >
      <div className="relative flex w-full">
        <div className="relative flex-grow">
          <input
            ref={inputRef}
            value={searchValue}
            onChange={(e) => handleInput(e)}
            onKeyDown={(e) => isEnterKey(e) && handleSubmit()}
            placeholder={fields?.searchBoxPlaceholder?.value || 'Search'}
            enterKeyHint="search"
            className={classNames(
              'border-2',
              'border-gray',
              'w-full',
              'rounded-l-lg',
              'p-3',
              'pr-10',
              'text-gray-dark',
              'focus:outline-none',
              'transition-all'
            )}
          />
          {searchValue && (
            <button
              className={classNames(
                'absolute',
                'top-1/2',
                'right-3',
                'transform',
                '-translate-y-1/2',
                'text-gray-medium'
              )}
              onClick={handleClear}
              title="Clear"
            >
              <HeroIcon size="xs" icon={'XMarkIcon'} />
            </button>
          )}
        </div>
        <button
          disabled={!searchValue}
          onClick={() => handleSubmit()}
          className={classNames(
            'bg-blue',
            'border-2',
            'border-blue',
            'disabled:bg-btn-disabled-bg-primary',
            'disabled:border-transparent',
            'disabled:text-btn-disabled-text-primary',
            'disabled:cursor-not-allowed',
            'p-3',
            'rounded-r-lg',
            'text-white',
            'transition-all'
          )}
          title="Search"
        >
          <HeroIcon size="xs" icon={'MagnifyingGlassIcon'} />
        </button>
      </div>

      {isSuggestionsVisible && (
        <AzBlueHeadlessAutoSuggestDropdown
          suggestions={suggestions}
          showClearRecentSearches={true}
          recentSearches={recentState.queries}
          searchValue={searchValue}
          onSelect={(value) => handleSubmit(value)}
          onClearRecentSearches={() => recentController.clear()}
        />
      )}
    </div>
  );
};
